import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ElementShape, LineView, layoutText, textBox, textHeightFor, DEFAULT_FS } from './ElementView'
import { AskOnlyToolbar, ContextBar, Toolbar, ZoomBar, type Tool } from './BoardTools'
import {
  attachAt,
  attachable,
  bottomZ,
  boxOf,
  clearSpot,
  contains,
  fitImage,
  hitTest,
  INK,
  intersects,
  isAttached,
  keepsShape,
  lineGeom,
  linesOf,
  midpoint,
  newId,
  NOTE_COLOURS,
  paintOrder,
  rowAt,
  SELECT,
  shapeSize,
  sidePoint,
  STICKY_SIZE,
  topZ,
  union,
  writable,
  writesText,
  type Box,
  type El,
  type End,
  type Op,
  type Pt,
  type ShapeKind,
  type Side,
} from './model'
import { BOARD_FONT, LINE_HEIGHT, textWidth } from './text'
import type { BoardApi } from './useBoard'
import { importText, isPicture, prepareImage, uploadImage } from './boardImages'
import { postBoardOps } from '../chatv1/transport'
import { Icon } from '../ui/Icon'
import type { MentionAgent } from '../agents/AgentMention'
import { AskBox, AskGlyph } from './AskBox'
import { areaPicture } from './areaPicture'
import { ASK, aroundItem, boxBetween, itemsIn, loadKept, onShowArea, placeAsk, saveKept, takeArea, type KeptAsk, type Shown } from './area'

/** A note's editor (BlockNote, as a page's): loaded the first time a note is written — fetched early, once a note is picked
 *  or the note tool is, so the words open without a wait. */
const NoteEditor = lazy(() => import('./NoteEditor'))
let noteEditorAsked = false
const prefetchNoteEditor = () => {
  if (noteEditorAsked) return
  noteEditorAsked = true
  void import('./NoteEditor')
}

/**
 * THE CANVAS — an infinite sheet you move about on, the way Miro's is (owner, 2026-10-29: 仔細研究一下 Miro 的 UI).
 *
 *   · pick a tool at the left; a click places it (a drag sizes it); the tool goes back to Select afterwards — and
 *     nothing is placed without its tool
 *   · a selected shape or note shows a dot on each side: press one to add the NEXT box of a flowchart, already
 *     joined to this one, or drag from it to draw a line to anything
 *   · drag to move (a frame takes what is inside it), corners to resize, double-click or just type to write
 *   · wheel / two fingers move the board, pinch or ⌘-wheel zooms; Space-drag or the Hand moves it too
 *   · pictures come in by the Image tool, a drop from the desktop, or a paste (owner, 2026-10-30: import image)
 *
 * Every change goes through the board hook as ops, so it is drawn at once here and sent to the room; a drag sends as
 * it goes (others watch it move) and is ONE undo step.
 */

type Cam = { x: number; y: number; s: number }
const MIN_S = 0.1
const MAX_S = 4
const GAP = 80

type Drag =
  | { kind: 'pan'; start: Pt; cam: Cam }
  | { kind: 'move'; start: Pt; ids: string[]; origin: Record<string, El>; before: Record<string, El>; moved: boolean }
  | { kind: 'resize'; start: Pt; id: string; corner: 'nw' | 'ne' | 'sw' | 'se'; box: Box; before: Record<string, El>; moved: boolean }
  | { kind: 'marquee'; start: Pt; at: Pt; add: boolean }
  | { kind: 'create'; start: Pt; at: Pt; tool: Tool }
  | { kind: 'line'; start: Pt; from: End; at: Pt; target: { id: string; side?: Side } | null; dot?: { id: string; side: Side } }
  | { kind: 'end'; start: Pt; id: string; which: 'a' | 'b'; at: Pt; target: { id: string; side?: Side } | null; before: Record<string, El> }
  | { kind: 'pen'; pts: Pt[] }
  | { kind: 'area'; start: Pt; at: Pt }

/** One element's copy, with a new id and an offset; `ids` maps old ids to new (lines between copies follow). */
function cloneAll(src: El[], dx: number, dy: number, z0: number): Op[] {
  const ids = new Map(src.map((e) => [e.id, newId()]))
  const ops: Op[] = []
  let z = z0
  for (const e of src) {
    const set = writable(e)
    set.x = e.x + dx
    set.y = e.y + dy
    set.z = ++z
    if (e.t === 'line') {
      const mapEnd = (end: End | undefined): End | undefined => {
        if (!end) return end
        if (isAttached(end)) return ids.has(end.id) ? { ...end, id: ids.get(end.id)! } : undefined
        return { x: end.x + dx, y: end.y + dy }
      }
      const a = mapEnd(e.a)
      const b = mapEnd(e.b)
      if (!a || !b) continue
      set.a = a
      set.b = b
    }
    ops.push({ id: ids.get(e.id)!, set })
  }
  return ops
}

let clipboard: El[] = []

/** How long a sent question's box stays, saying it went, before it leaves the board (when it is not kept). */
const SENT_MS = 1600
/** The question window's size, for placing it beside the box (board.css .bd-ask). */
const ASK_WIN = { w: 360, h: 176 }

export function BoardCanvas({
  board,
  phone,
  readOnly,
  docked,
  convId,
  boardId,
  ask,
}: {
  board: BoardApi
  phone: boolean
  readOnly?: boolean
  /** Beside a conversation (the session's drawer, BoardDrawer): only keys and pastes pressed in the drawer are the
   *  board's — the composer, the transcript and the terminal keep theirs. */
  docked?: boolean
  convId: string
  boardId: string
  /** Questions about an area of this board (board/area.ts): the board's name and the room's agents for `@`, and the
   *  way to the room's chat over the board, where the answers come. Absent: no Ask tool. */
  ask?: { boardName: string; agents: MentionAgent[]; onOpenChat: () => void }
}) {
  const { els } = board
  const stage = useRef<HTMLDivElement>(null)
  const [cam, setCam] = useState<Cam>({ x: 0, y: 0, s: 1 })
  const camRef = useRef(cam)
  camRef.current = cam
  const [size, setSize] = useState({ w: 800, h: 600 })
  const [tool, setTool] = useState<Tool>('select')
  const [shape, setShape] = useState<ShapeKind>('rect')
  const [note, setNote] = useState(NOTE_COLOURS[0]!.hex)
  const [sel, setSel] = useState<string[]>([])
  // The note's editor, fetched before it is needed: the note tool picked, or a note picked.
  useEffect(() => {
    if (tool === 'sticky' || sel.some((id) => elsRef.current[id]?.t === 'sticky')) prefetchNoteEditor()
  }, [tool, sel])
  const [editing, setEditing] = useState<{ id: string; text: string; created?: { before: Record<string, El>; ops: Op[] } } | null>(null)
  const [drag, setDragState] = useState<Drag | null>(null)
  const dragRef = useRef<Drag | null>(null)
  const setDrag = (d: Drag | null) => {
    dragRef.current = d
    setDragState(d)
  }
  const [space, setSpace] = useState(false)
  const pointers = useRef(new Map<number, Pt>())
  const pinch = useRef<{ d: number; mid: Pt; cam: Cam } | null>(null)
  const elsRef = useRef(els)
  elsRef.current = els
  const fitted = useRef(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(0)
  /** The board's layer of items — what a question's picture is made from (areaPicture.ts). */
  const layer = useRef<SVGGElement>(null)
  /** A question being written: the area boxed and the items in it. */
  const [asking, setAsking] = useState<{ area: Box; ids: string[] } | null>(null)
  /** Questions kept on the board after sending (this device's; area.ts). */
  const [kept, setKeptState] = useState<KeptAsk[]>(() => loadKept(convId, boardId))
  const setKept = (fn: (cur: KeptAsk[]) => KeptAsk[]) =>
    setKeptState((cur) => {
      const next = fn(cur)
      saveKept(convId, boardId, next)
      return next
    })
  /** The kept question whose card is open. */
  const [openKept, setOpenKept] = useState<string | null>(null)
  /** A question just sent, on its way off the board. */
  const [sent, setSent] = useState<Box | null>(null)
  /** An area shown again from the chat (area.ts showArea). */
  const [shown, setShown] = useState<Shown | null>(null)
  // One canvas per board (BoardScreen keys it): gone means another board was picked, or the board was closed.
  const here = useRef(true)
  useEffect(() => {
    here.current = true
    return () => {
      here.current = false
    }
  }, [])

  // Selection follows the board: what someone else deleted is no longer selected.
  useEffect(() => {
    setSel((cur) => {
      const next = cur.filter((id) => els[id])
      return next.length === cur.length ? cur : next
    })
    if (editing && !els[editing.id]) setEditing(null)
  }, [els, editing])

  useLayoutEffect(() => {
    const el = stage.current
    if (!el) return
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }))
    ro.observe(el)
    setSize({ w: el.clientWidth, h: el.clientHeight })
    return () => ro.disconnect()
  }, [])

  const toWorld = useCallback((cx: number, cy: number): Pt => {
    const r = stage.current!.getBoundingClientRect()
    const c = camRef.current
    return { x: (cx - r.left - c.x) / c.s, y: (cy - r.top - c.y) / c.s }
  }, [])

  const zoomAt = useCallback((factor: number, sx?: number, sy?: number) => {
    setCam((c) => {
      const s = Math.min(MAX_S, Math.max(MIN_S, c.s * factor))
      const px = sx ?? (stage.current?.clientWidth ?? 0) / 2
      const py = sy ?? (stage.current?.clientHeight ?? 0) / 2
      const wx = (px - c.x) / c.s
      const wy = (py - c.y) / c.s
      return { s, x: px - wx * s, y: py - wy * s }
    })
  }, [])

  const fit = useCallback(() => {
    const all = Object.values(elsRef.current)
      .map((e) => boxOf(e, elsRef.current))
      .filter((b): b is Box => !!b)
    const box = union(all)
    const w = stage.current?.clientWidth ?? 800
    const h = stage.current?.clientHeight ?? 600
    if (!box) {
      // An empty board on a phone starts zoomed out: a note is 200 px at 100 %, half a phone's width.
      setCam({ x: w / 2, y: h / 2, s: phone ? 0.6 : 1 })
      return
    }
    const pad = phone ? 32 : 96
    const s = Math.min(1, Math.max(MIN_S, Math.min((w - 2 * pad) / Math.max(1, box.w), (h - 2 * pad) / Math.max(1, box.h))))
    setCam({ s, x: w / 2 - (box.x + box.w / 2) * s, y: h / 2 - (box.y + box.h / 2) * s })
  }, [phone])

  /** The view on one area, with room round it — at most 150 %. */
  const fitTo = useCallback((box: Box) => {
    const w = stage.current?.clientWidth ?? 800
    const h = stage.current?.clientHeight ?? 600
    const pad = phone ? 40 : 140
    const s = Math.min(1.5, Math.max(MIN_S, Math.min((w - 2 * pad) / Math.max(1, box.w), (h - 2 * pad) / Math.max(1, box.h))))
    setCam({ s, x: w / 2 - (box.x + box.w / 2) * s, y: h / 2 - (box.y + box.h / 2) * s })
  }, [phone])

  // The first time there is a board to look at, show all of it — or the area a message's chip asked to see.
  useEffect(() => {
    if (fitted.current || board.status !== 'ready') return
    fitted.current = true
    const s = takeArea(convId, boardId)
    if (s) {
      setShown(s)
      fitTo(s.area)
    } else fit()
  }, [board.status, boardId, convId, fit, fitTo])
  // A chip pressed while this board is already open: the view goes to the area at once.
  useEffect(
    () =>
      onShowArea(() => {
        if (!fitted.current) return
        const s = takeArea(convId, boardId)
        if (!s) return
        setShown(s)
        fitTo(s.area)
      }),
    [boardId, convId, fitTo],
  )
  // A sent question's box says it went, then leaves.
  useEffect(() => {
    if (!sent) return
    const t = setTimeout(() => setSent(null), SENT_MS)
    return () => clearTimeout(t)
  }, [sent])

  // Wheel and Safari's pinch: not passive, so the page itself never zooms or scrolls under the board.
  useEffect(() => {
    const el = stage.current
    if (!el) return
    const wheel = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      if (e.ctrlKey || e.metaKey) zoomAt(Math.exp(-e.deltaY * 0.01), e.clientX - r.left, e.clientY - r.top)
      else setCam((c) => ({ ...c, x: c.x - e.deltaX, y: c.y - e.deltaY }))
    }
    let gs: Cam | null = null
    const gstart = (e: Event) => {
      e.preventDefault()
      gs = camRef.current
    }
    const gchange = (e: Event) => {
      e.preventDefault()
      const ge = e as Event & { scale: number; clientX: number; clientY: number }
      // On a phone two fingers ALSO arrive as pointers, and that pinch is already moving the board.
      if (!gs || pinch.current) return
      const r = el.getBoundingClientRect()
      const s = Math.min(MAX_S, Math.max(MIN_S, gs.s * ge.scale))
      const px = ge.clientX - r.left
      const py = ge.clientY - r.top
      const wx = (px - gs.x) / gs.s
      const wy = (py - gs.y) / gs.s
      setCam({ s, x: px - wx * s, y: py - wy * s })
    }
    el.addEventListener('wheel', wheel, { passive: false })
    el.addEventListener('gesturestart', gstart)
    el.addEventListener('gesturechange', gchange)
    return () => {
      el.removeEventListener('wheel', wheel)
      el.removeEventListener('gesturestart', gstart)
      el.removeEventListener('gesturechange', gchange)
    }
  }, [zoomAt])

  // ── writing ──────────────────────────────────────────────────────────────

  // Locked to this person while they are at it (board/EditLock): what they had in hand stops; the board is theirs to look at.
  useEffect(() => {
    if (!readOnly) return
    setEditing(null)
    setSel([])
    setTool('select')
    setDrag(null)
  }, [readOnly])

  const write = useCallback((ops: Op[]) => {
    if (!readOnly) board.apply(ops)
  }, [board, readOnly])

  const deleteIds = useCallback(
    (ids: string[]) => {
      const set = new Set(ids)
      const lines = linesOf(set, elsRef.current).map((l) => l.id)
      const all = [...new Set([...ids, ...lines])]
      write(all.map((id) => ({ id, del: true as const })))
      setSel([])
    },
    [write],
  )

  const startEdit = useCallback((id: string, text?: string, created?: { before: Record<string, El>; ops: Op[] }) => {
    // Something made a moment ago is not on the board this render yet; its own op says what it is.
    const el = elsRef.current[id] ?? (created?.ops.find((o) => o.id === id)?.set as El | undefined)
    if (!el || !writesText(el) || readOnly) return
    setSel([id])
    // Already open (a finger's double-tap, then the browser's own double-click for it): the words typed stay.
    setEditing((cur) => (cur?.id === id && !created ? cur : { id, text: text ?? el.text ?? '', ...(created ? { created } : {}) }))
  }, [readOnly])

  const commitEdit = useCallback(() => {
    const ed = editing
    if (!ed) return
    setEditing(null)
    const el = elsRef.current[ed.id]
    if (!el) return
    const text = ed.text.replace(/\s+$/, '')
    if (ed.created) {
      // A new element and its first words are ONE undo step.
      if (!text && el.t === 'text') {
        board.apply([{ id: ed.id, del: true }], { history: 'none' })
        setSel([])
        return
      }
      const set: Op['set'] = { text }
      if (el.t === 'text') set.h = Math.ceil(textHeightFor({ ...el, text }))
      if (text) board.apply([{ id: ed.id, set }], { history: 'none' })
      const first = ed.created.ops[0]!
      board.record(ed.created.before, [{ ...first, set: { ...(first.set ?? {}), ...(text ? set : {}) } }, ...ed.created.ops.slice(1)])
      return
    }
    if (text === (el.text ?? '')) return
    const set: Op['set'] = { text }
    if (el.t === 'text') set.h = Math.ceil(textHeightFor({ ...el, text }))
    write([{ id: ed.id, set }])
  }, [board, editing, write])

  /** Place a new element; text-bearing ones open for typing at once. */
  const create = useCallback(
    (t: Tool, box: Box | null, at: Pt) => {
      if (readOnly) return
      const before = elsRef.current
      const id = newId()
      const z = topZ(before) + 1
      let set: Op['set']
      if (t === 'sticky') {
        // Dragged out, the note is the box drawn (a note takes any shape); clicked, the usual square.
        const w = box ? Math.max(box.w, 60) : STICKY_SIZE
        const h = box ? Math.max(box.h, 60) : STICKY_SIZE
        set = { t: 'sticky', x: box?.x ?? at.x - w / 2, y: box?.y ?? at.y - h / 2, w, h, z, fill: note, text: '' }
      } else if (t === 'shape') {
        const d = shapeSize(shape)
        const w = box ? Math.max(box.w, 24) : d.w
        const h = box ? Math.max(box.h, 24) : d.h
        set = { t: 'shape', shape, x: box?.x ?? at.x - w / 2, y: box?.y ?? at.y - h / 2, w, h, z, fill: '#ffffff', stroke: INK, sw: 2, text: '' }
      } else if (t === 'text') {
        const w = box ? Math.max(box.w, 60) : 240
        set = { t: 'text', x: box?.x ?? at.x, y: box?.y ?? at.y - 14, w, h: 28, z, text: '' }
      } else if (t === 'frame') {
        const w = box ? Math.max(box.w, 80) : 800
        const h = box ? Math.max(box.h, 60) : 500
        const n = Object.values(before).filter((e) => e.t === 'frame').length + 1
        set = { t: 'frame', x: box?.x ?? at.x - w / 2, y: box?.y ?? at.y - h / 2, w, h, z: bottomZ(before) - 1, text: `Frame ${n}`, fill: '#ffffff' }
      } else return
      // Whole pixels: what an agent reads back with `avengents board show` stays readable.
      for (const k of ['x', 'y', 'w', 'h'] as const) if (typeof set[k] === 'number') set[k] = Math.round(set[k] as number)
      const ops: Op[] = [{ id, set }]
      board.apply(ops, { history: t === 'frame' ? 'push' : 'none' })
      setTool('select')
      if (t === 'frame') setSel([id])
      else startEdit(id, '', { before, ops })
    },
    [board, note, readOnly, shape, startEdit],
  )

  /**
   * Miro's quick connect: the next box of the flowchart, the same kind and size, a gap away on that side, joined to
   * this one by a line — and open for typing.
   */
  const quickConnect = useCallback(
    (fromId: string, side: Side) => {
      const src = elsRef.current[fromId]
      if (!src || readOnly) return
      const before = elsRef.current
      const id = newId()
      const lineId = newId()
      let x = src.x
      let y = src.y
      if (side === 'right') x = src.x + src.w + GAP
      if (side === 'left') x = src.x - src.w - GAP
      if (side === 'bottom') y = src.y + src.h + GAP
      if (side === 'top') y = src.y - src.h - GAP
      // Step further out while the spot is taken, so a second press does not stack on the first.
      for (let i = 0; i < 12; i++) {
        const spot = { x, y, w: src.w, h: src.h }
        const taken = Object.values(elsRef.current).some((e) => e.t !== 'line' && e.t !== 'frame' && e.id !== fromId && intersects(spot, e))
        if (!taken) break
        if (side === 'left' || side === 'right') y += src.h + GAP / 2
        else x += src.w + GAP / 2
      }
      const opposite: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }
      const z = topZ(before) + 1
      const set: Op['set'] = { ...writable(src), x, y, z: z + 1, text: '' }
      delete set.fs
      const ops: Op[] = [
        { id, set },
        { id: lineId, set: { t: 'line', a: { id: fromId, side }, b: { id, side: opposite[side] }, route: 'elbow', head: 'arrow', stroke: INK, sw: 2, z, x: 0, y: 0, w: 1, h: 1 } },
      ]
      board.apply(ops, { history: 'none' })
      startEdit(id, '', { before, ops })
    },
    [board, readOnly, startEdit],
  )

  const duplicate = useCallback(() => {
    const src = sel.map((id) => elsRef.current[id]).filter((e): e is El => !!e)
    if (!src.length) return
    const withLines = [...src, ...linesOf(new Set(sel), elsRef.current).filter((l) => !sel.includes(l.id))]
    const ops = cloneAll(withLines, 24, 24, topZ(elsRef.current))
    write(ops)
    setSel(ops.filter((o) => o.set?.t !== 'line' || src.some((s) => s.t === 'line')).map((o) => o.id))
  }, [sel, write])

  const paste = useCallback(() => {
    if (!clipboard.length) return
    const box = union(clipboard.map((e) => ({ x: e.x, y: e.y, w: e.w, h: e.h })))!
    const c = camRef.current
    const cx = (size.w / 2 - c.x) / c.s
    const cy = (size.h / 2 - c.y) / c.s
    const ops = cloneAll(clipboard, cx - (box.x + box.w / 2), cy - (box.y + box.h / 2), topZ(elsRef.current))
    write(ops)
    setSel(ops.map((o) => o.id))
  }, [size, write])

  const restack = useCallback(
    (front: boolean) => {
      let z = front ? topZ(elsRef.current) : bottomZ(elsRef.current)
      const ops = sel
        .map((id) => elsRef.current[id])
        .filter((e): e is El => !!e)
        .sort((a, b) => a.z - b.z)
      const ordered = front ? ops : [...ops].reverse()
      write(ordered.map((e) => ({ id: e.id, set: { z: front ? ++z : --z } })))
    },
    [sel, write],
  )

  /**
   * Pictures onto the board at `at` (default: the middle of the screen, or the nearest clear spot to it), side by side
   * when there are several, each no bigger than most of the screen. ONE undo step, and they come in selected. Each is uploaded and claimed before its
   * element is written, so the room never sees an element whose picture it may not read.
   */
  const importImages = useCallback(
    async (files: File[], at?: Pt) => {
      const pics = files.filter(isPicture)
      if (readOnly || !pics.length) return
      const onBoard = boardId
      setUploading((n) => n + pics.length)
      const done = await Promise.allSettled(
        pics.map(async (f) => {
          const p = await prepareImage(f)
          const src = await uploadImage(convId, onBoard, p)
          return { src, name: p.name, w: p.w, h: p.h }
        }),
      )
      setUploading((n) => n - pics.length)
      const ok = done.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []))
      const failed = done.find((r): r is PromiseRejectedResult => r.status === 'rejected')
      if (failed) board.notify(importText(failed.reason))
      if (!ok.length) return
      const c = camRef.current
      const view = { w: stage.current?.clientWidth ?? 800, h: stage.current?.clientHeight ?? 600 }
      const centre = at ?? { x: (view.w / 2 - c.x) / c.s, y: (view.h / 2 - c.y) / c.s }
      const each = (0.6 * view.w) / c.s / ok.length
      const sizes = ok.map((p) => fitImage(p.w, p.h, each, (0.6 * view.h) / c.s))
      let boxes = rowAt(sizes, centre, 24)
      // Not dropped anywhere in particular: beside what is already there rather than on top of it.
      if (!at) {
        const off = clearSpot(union(boxes)!, elsRef.current)
        boxes = boxes.map((b) => ({ ...b, x: b.x + off.x, y: b.y + off.y }))
      }
      if (!here.current) {
        // Another board was opened while they uploaded (the hook writes to THAT one now): they still go to the board
        // they were brought to — whose picture this gone canvas still holds — straight to the service.
        let z0 = topZ(elsRef.current)
        const ops: Op[] = ok.map((p, i) => ({ id: newId(), set: { t: 'image', ...boxes[i]!, z: ++z0, src: p.src, name: p.name.slice(0, 200) } }))
        postBoardOps(convId, onBoard, ops, newId()).catch(() => undefined)
        board.notify(ok.length === 1 ? 'The picture went to the board it was added on.' : 'The pictures went to the board they were added on.')
        return
      }
      // Placed by the board rather than by a drop: the view follows them when that is off the screen.
      const u = union(boxes)!
      const seen = { x: -c.x / c.s, y: -c.y / c.s, w: view.w / c.s, h: view.h / c.s }
      if (!at && !contains(seen, u)) setCam({ s: c.s, x: view.w / 2 - (u.x + u.w / 2) * c.s, y: view.h / 2 - (u.y + u.h / 2) * c.s })
      let z = topZ(elsRef.current)
      const ops: Op[] = ok.map((p, i) => ({
        id: newId(),
        set: { t: 'image', ...boxes[i]!, z: ++z, src: p.src, name: p.name.slice(0, 200) },
      }))
      board.apply(ops)
      setTool('select')
      setSel(ops.map((o) => o.id))
    },
    [board, boardId, convId, readOnly],
  )

  // A picture pasted from anywhere (a screenshot, a copied image) lands in the middle of the screen; a paste with no
  // picture in it pastes what was copied on the board. Browsers that never fire a paste here (nothing editable has
  // focus) still paste the board's own copy, by the key alone.
  const pasteTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      if (readOnly || document.querySelector('.sheet-scrim')) return
      if (docked && !target?.closest?.('.bd-drawer')) return
      if (pasteTimer.current) clearTimeout(pasteTimer.current)
      pasteTimer.current = null
      const files = [...(e.clipboardData?.files ?? [])].filter(isPicture)
      e.preventDefault()
      if (files.length) void importImages(files)
      else paste()
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [docked, importImages, paste, readOnly])

  // ── keys ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      // The board menu takes its own keys (a Delete there is not a Delete on the board).
      if (document.querySelector('.sheet-scrim') || target?.closest?.('.bd-menu')) return
      if (docked && !target?.closest?.('.bd-drawer')) return
      const mod = e.metaKey || e.ctrlKey
      const k = e.key
      if (k === ' ' && !e.repeat) {
        setSpace(true)
        e.preventDefault()
        return
      }
      if (mod && k.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) board.redo()
        else board.undo()
        return
      }
      if (mod && k.toLowerCase() === 'y') {
        e.preventDefault()
        board.redo()
        return
      }
      if (mod && (k === '=' || k === '+')) {
        e.preventDefault()
        zoomAt(1.25)
        return
      }
      if (mod && k === '-') {
        e.preventDefault()
        zoomAt(1 / 1.25)
        return
      }
      if (e.shiftKey && (k === '!' || e.code === 'Digit1')) {
        e.preventDefault()
        fit()
        return
      }
      if (e.shiftKey && (k === ')' || e.code === 'Digit0')) {
        e.preventDefault()
        zoomAt(1 / camRef.current.s)
        return
      }
      if (mod && k.toLowerCase() === 'a') {
        e.preventDefault()
        setSel(Object.keys(elsRef.current))
        return
      }
      if (readOnly) {
        // Looking only: the question about an area is all there is to pick, and Escape puts it down.
        if (k === 'Escape') {
          setTool('select')
          setAsking(null)
          setShown(null)
        } else if (ask && !mod && !e.altKey && k.toLowerCase() === 'a') setTool((t) => (t === 'ask' ? 'select' : 'ask'))
        return
      }
      if (mod && k.toLowerCase() === 'd') {
        e.preventDefault()
        duplicate()
        return
      }
      if (mod && k.toLowerCase() === 'c') {
        clipboard = sel.map((id) => elsRef.current[id]).filter((x): x is El => !!x)
        const lines = linesOf(new Set(sel), elsRef.current).filter((l) => !sel.includes(l.id))
        clipboard.push(...lines)
        return
      }
      if (mod && k.toLowerCase() === 'v') {
        // Not prevented: the paste event that follows may carry a picture (above). Without one, the board's copy.
        if (pasteTimer.current) clearTimeout(pasteTimer.current)
        pasteTimer.current = setTimeout(() => {
          pasteTimer.current = null
          paste()
        }, 120)
        return
      }
      if ((k === 'Delete' || k === 'Backspace') && sel.length) {
        e.preventDefault()
        deleteIds(sel)
        return
      }
      if (k === 'Escape') {
        setSel([])
        setTool('select')
        setAsking(null)
        setShown(null)
        return
      }
      if (k.startsWith('Arrow') && sel.length) {
        e.preventDefault()
        const d = e.shiftKey ? 10 : 1
        const dx = k === 'ArrowLeft' ? -d : k === 'ArrowRight' ? d : 0
        const dy = k === 'ArrowUp' ? -d : k === 'ArrowDown' ? d : 0
        write(
          sel
            .map((id) => elsRef.current[id])
            .filter((x): x is El => !!x && x.t !== 'line')
            .map((x) => ({ id: x.id, set: { x: x.x + dx, y: x.y + dy } })),
        )
        return
      }
      if (k === 'Enter' && sel.length === 1) {
        e.preventDefault()
        startEdit(sel[0]!)
        return
      }
      if (mod || e.altKey) return
      // Letters choose tools even with something selected — never "typing replaces the selected note's words": one
      // press of T for the text tool would wipe a note. Writing into a box is Enter, a double-click, or right after
      // placing it (the editor opens by itself).
      const tools: Record<string, Tool> = { v: 'select', h: 'hand', n: 'sticky', t: 'text', s: 'shape', r: 'shape', l: 'line', p: 'pen', f: 'frame', ...(ask ? { a: 'ask' as const } : {}) }
      const tk = tools[k.toLowerCase()]
      if (tk) {
        if (k.toLowerCase() === 'r') setShape('rect')
        setTool(tk)
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.key === ' ') setSpace(false)
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [ask, board, deleteIds, docked, duplicate, fit, paste, readOnly, sel, startEdit, write, zoomAt])

  // ── pointer ─────────────────────────────────────────────────────────────

  const slack = 6 / cam.s

  /**
   * What a line end dropped at `p` holds on to: the element under it — anywhere on it when dropped mid-box, else the
   * very point of the edge it was dropped at (Miro's way; the side's middle within 8 screen pixels of it).
   */
  const targetAt = (p: Pt, skip?: string): { id: string; side?: Side; t?: number } | null => {
    const hit = hitTest(elsRef.current, p, slack, { attachOnly: true, skip: skip ? new Set([skip]) : undefined })
    return hit ? attachAt(hit, p, 8 / camRef.current.s) : null
  }

  /**
   * A finger's double-tap. The board owns its touches (touch-action: none), so a browser makes no double-click of two
   * taps — and on a phone a note could not be opened for writing (found checking the note's editor on a phone, 2026-11-02).
   * Two taps on one spot within 350 ms are one here: the words of what they land on open, as a double-click's do.
   */
  const tapAt = useRef<{ x: number; y: number; t: number } | null>(null)
  const lastTap = useRef<{ x: number; y: number; t: number } | null>(null)
  const onTouchTap = (e: React.PointerEvent) => {
    const down = tapAt.current
    tapAt.current = null
    if (e.pointerType !== 'touch' || !down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 10 || e.timeStamp - down.t > 400) return
    const prev = lastTap.current
    lastTap.current = { x: e.clientX, y: e.clientY, t: e.timeStamp }
    if (!prev || e.timeStamp - prev.t > 350 || Math.hypot(e.clientX - prev.x, e.clientY - prev.y) > 24) return
    lastTap.current = null
    if (readOnly) return
    const hit = hitTest(elsRef.current, toWorld(e.clientX, e.clientY), slack)
    if (hit && writesText(hit)) startEdit(hit.id)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.isPrimary && e.pointerType === 'touch') tapAt.current = { x: e.clientX, y: e.clientY, t: e.timeStamp }
    if (e.button === 2) return
    const el = stage.current!
    el.setPointerCapture(e.pointerId)
    // The first finger of a new touch: whatever a lost pointerup left behind is not a second finger.
    if (e.isPrimary) {
      pointers.current.clear()
      pinch.current = null
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (editing) commitEdit()
    if (pointers.current.size === 2) {
      // Two fingers: the board follows them — whatever one finger had begun stops where it is.
      const [p1, p2] = [...pointers.current.values()] as [Pt, Pt]
      const r = el.getBoundingClientRect()
      pinch.current = { d: Math.hypot(p2.x - p1.x, p2.y - p1.y), mid: { x: (p1.x + p2.x) / 2 - r.left, y: (p1.y + p2.y) / 2 - r.top }, cam: camRef.current }
      finishDrag(true)
      return
    }
    const p = toWorld(e.clientX, e.clientY)
    const h = (e.target as Element).closest('[data-h]')?.getAttribute('data-h') ?? null
    // Nothing to do on a board this person may only look at (its lock) but look around it — and ask about an area of it.
    if (e.button === 1 || space || tool === 'hand' || (readOnly && tool !== 'ask')) {
      setDrag({ kind: 'pan', start: { x: e.clientX, y: e.clientY }, cam: camRef.current })
      return
    }
    if (h && !readOnly) {
      const one = sel.length === 1 ? elsRef.current[sel[0]!] : undefined
      if (one && h.startsWith('dot-')) {
        const side = h.slice(4) as Side
        setDrag({ kind: 'line', start: p, from: { id: one.id, side }, at: p, target: null, dot: { id: one.id, side } })
        return
      }
      if (one && (h === 'nw' || h === 'ne' || h === 'sw' || h === 'se')) {
        setDrag({ kind: 'resize', start: p, id: one.id, corner: h, box: { x: one.x, y: one.y, w: one.w, h: one.h }, before: elsRef.current, moved: false })
        return
      }
      if (one && (h === 'end-a' || h === 'end-b')) {
        setDrag({ kind: 'end', start: p, id: one.id, which: h === 'end-a' ? 'a' : 'b', at: p, target: null, before: elsRef.current })
        return
      }
    }
    if (tool === 'sticky' || tool === 'shape' || tool === 'text' || tool === 'frame') {
      setDrag({ kind: 'create', start: p, at: p, tool })
      return
    }
    if (tool === 'ask') {
      setDrag({ kind: 'area', start: p, at: p })
      return
    }
    if (tool === 'line') {
      const t = targetAt(p)
      setDrag({ kind: 'line', start: p, from: t ?? { x: p.x, y: p.y }, at: p, target: null })
      return
    }
    if (tool === 'pen') {
      setDrag({ kind: 'pen', pts: [p] })
      return
    }
    // Select.
    const hit = hitTest(elsRef.current, p, slack)
    if (hit) {
      let ids = sel
      if (e.shiftKey) {
        ids = sel.includes(hit.id) ? sel.filter((x) => x !== hit.id) : [...sel, hit.id]
        setSel(ids)
        return
      }
      if (!sel.includes(hit.id)) {
        ids = [hit.id]
        setSel(ids)
      }
      if (readOnly) return
      // A frame carries what stands inside it.
      const moving = new Set(ids)
      for (const id of ids) {
        const f = elsRef.current[id]
        if (f?.t !== 'frame') continue
        for (const o of Object.values(elsRef.current)) {
          if (o.t === 'line' || moving.has(o.id)) continue
          if (contains(f, o)) moving.add(o.id)
        }
      }
      // A line moves with its ends' elements; a free end moves when the line itself is being moved.
      const origin: Record<string, El> = {}
      for (const id of moving) if (elsRef.current[id]) origin[id] = elsRef.current[id]!
      setDrag({ kind: 'move', start: p, ids: [...moving], origin, before: elsRef.current, moved: false })
      return
    }
    if (e.pointerType === 'touch') {
      setSel([])
      setDrag({ kind: 'pan', start: { x: e.clientX, y: e.clientY }, cam: camRef.current })
      return
    }
    if (!e.shiftKey) setSel([])
    setDrag({ kind: 'marquee', start: p, at: p, add: e.shiftKey })
  }

  const moveOps = (d: Extract<Drag, { kind: 'move' }>, dx: number, dy: number): Op[] => {
    const ops: Op[] = []
    for (const id of d.ids) {
      const o = d.origin[id]
      if (!o) continue
      if (o.t === 'line') {
        const set: Op['set'] = {}
        if (o.a && !isAttached(o.a)) set.a = { x: o.a.x + dx, y: o.a.y + dy }
        if (o.b && !isAttached(o.b)) set.b = { x: o.b.x + dx, y: o.b.y + dy }
        if (Object.keys(set).length) ops.push({ id, set })
        continue
      }
      ops.push({ id, set: { x: Math.round(o.x + dx), y: Math.round(o.y + dy) } })
    }
    return ops
  }

  const resizeOps = (d: Extract<Drag, { kind: 'resize' }>, p: Pt, square: boolean): Op[] => {
    const b = d.box
    const dx = p.x - d.start.x
    const dy = p.y - d.start.y
    let x0 = b.x
    let y0 = b.y
    let x1 = b.x + b.w
    let y1 = b.y + b.h
    if (d.corner.includes('w')) x0 += dx
    else x1 += dx
    if (d.corner.includes('n')) y0 += dy
    else y1 += dy
    let w = Math.max(20, x1 - x0)
    let h = Math.max(20, y1 - y0)
    if (square) {
      const k = Math.max(w / b.w, h / b.h)
      w = Math.max(20, b.w * k)
      h = Math.max(20, b.h * k)
    }
    const x = d.corner.includes('w') ? b.x + b.w - w : b.x
    const y = d.corner.includes('n') ? b.y + b.h - h : b.y
    return [{ id: d.id, set: { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) } }]
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinch.current && pointers.current.size >= 2) {
      const [p1, p2] = [...pointers.current.values()] as [Pt, Pt]
      const r = stage.current!.getBoundingClientRect()
      const pc = pinch.current
      const d = Math.hypot(p2.x - p1.x, p2.y - p1.y)
      const mid = { x: (p1.x + p2.x) / 2 - r.left, y: (p1.y + p2.y) / 2 - r.top }
      const s = Math.min(MAX_S, Math.max(MIN_S, pc.cam.s * (d / Math.max(1, pc.d))))
      const wx = (pc.mid.x - pc.cam.x) / pc.cam.s
      const wy = (pc.mid.y - pc.cam.y) / pc.cam.s
      setCam({ s, x: mid.x - wx * s, y: mid.y - wy * s })
      return
    }
    const d = dragRef.current
    if (!d) return
    if (d.kind === 'pan') {
      setCam({ ...d.cam, x: d.cam.x + e.clientX - d.start.x, y: d.cam.y + e.clientY - d.start.y })
      return
    }
    const p = toWorld(e.clientX, e.clientY)
    switch (d.kind) {
      case 'move': {
        const dx = p.x - d.start.x
        const dy = p.y - d.start.y
        if (!d.moved && Math.hypot(dx, dy) * camRef.current.s < 3) return
        dragRef.current = { ...d, moved: true }
        board.apply(moveOps(d, dx, dy), { history: 'none', live: true })
        return
      }
      case 'resize': {
        const el = elsRef.current[d.id]
        dragRef.current = { ...d, moved: true }
        board.apply(resizeOps(d, p, e.shiftKey || keepsShape(el)), { history: 'none', live: true })
        return
      }
      case 'marquee':
      case 'create':
      case 'area':
        setDrag({ ...d, at: p })
        return
      case 'line':
        setDrag({ ...d, at: p, target: targetAt(p, isAttached(d.from) ? d.from.id : undefined) })
        return
      case 'end': {
        const line = elsRef.current[d.id]
        const other = line?.[d.which === 'a' ? 'b' : 'a']
        setDrag({ ...d, at: p, target: targetAt(p, isAttached(other) ? other.id : undefined) })
        return
      }
      case 'pen': {
        const last = d.pts[d.pts.length - 1]!
        if (Math.hypot(p.x - last.x, p.y - last.y) * camRef.current.s < 2) return
        setDrag({ kind: 'pen', pts: [...d.pts, p] })
        return
      }
    }
  }

  /** End what a drag was doing. `cancel`: a second finger came down — keep what was done, start nothing new. */
  const finishDrag = (cancel = false, at?: Pt, e?: { shiftKey: boolean }) => {
    const d = dragRef.current
    setDrag(null)
    if (!d) return
    const p = at ?? ('at' in d ? d.at : { x: 0, y: 0 })
    switch (d.kind) {
      case 'move': {
        if (!d.moved) return
        const ops = moveOps(d, p.x - d.start.x, p.y - d.start.y)
        board.apply(ops, { history: 'none' })
        board.record(d.before, ops)
        return
      }
      case 'resize': {
        if (!d.moved) return
        const el = elsRef.current[d.id]
        const ops = resizeOps(d, p, !!e?.shiftKey || keepsShape(el))
        board.apply(ops, { history: 'none' })
        board.record(d.before, ops)
        return
      }
      case 'marquee': {
        if (cancel) return
        const box = { x: Math.min(d.start.x, p.x), y: Math.min(d.start.y, p.y), w: Math.abs(p.x - d.start.x), h: Math.abs(p.y - d.start.y) }
        if (box.w * camRef.current.s < 4 && box.h * camRef.current.s < 4) return
        const picked = paintOrder(elsRef.current)
          .filter((x) => {
            const b = boxOf(x, elsRef.current)
            if (!b) return false
            return x.t === 'frame' ? contains(box, b) : intersects(box, b)
          })
          .map((x) => x.id)
        setSel((cur) => (d.add ? [...new Set([...cur, ...picked])] : picked))
        return
      }
      case 'create': {
        if (cancel) return
        const box = { x: Math.min(d.start.x, p.x), y: Math.min(d.start.y, p.y), w: Math.abs(p.x - d.start.x), h: Math.abs(p.y - d.start.y) }
        const dragged = box.w * camRef.current.s > 8 || box.h * camRef.current.s > 8
        create(d.tool, dragged ? box : null, d.start)
        return
      }
      case 'line': {
        if (cancel) return
        const moved = Math.hypot(p.x - d.start.x, p.y - d.start.y) * camRef.current.s > 6
        if (!moved) {
          if (d.dot) quickConnect(d.dot.id, d.dot.side)
          return
        }
        const b: End = d.target ?? { x: Math.round(p.x), y: Math.round(p.y) }
        const id = newId()
        write([{ id, set: { t: 'line', a: d.from, b, route: 'elbow', head: 'arrow', stroke: INK, sw: 2, z: topZ(elsRef.current) + 1, x: 0, y: 0, w: 1, h: 1 } }])
        setSel([id])
        setTool('select')
        return
      }
      case 'end': {
        if (cancel) return
        const end: End = d.target ?? { x: Math.round(p.x), y: Math.round(p.y) }
        const ops: Op[] = [{ id: d.id, set: { [d.which]: end } }]
        board.apply(ops, { history: 'none' })
        board.record(d.before, ops)
        return
      }
      case 'area': {
        if (cancel) return
        // A drag boxes an area; a tap on an item asks about that item; a tap on nothing waits for a drag.
        let area: Box | null = null
        if (Math.abs(p.x - d.start.x) * camRef.current.s > 8 || Math.abs(p.y - d.start.y) * camRef.current.s > 8) area = boxBetween(d.start, p)
        else {
          const hit = hitTest(elsRef.current, p, slack)
          const b = hit ? boxOf(hit, elsRef.current) : null
          if (b) area = aroundItem(b)
        }
        if (!area) return
        setAsking({ area, ids: itemsIn(area, elsRef.current) })
        setShown(null)
        setTool('select')
        return
      }
      case 'pen': {
        if (cancel || d.pts.length < 2 || readOnly) return
        const box = union(d.pts.map((q) => ({ x: q.x, y: q.y, w: 0, h: 0 })))!
        const pts: number[] = []
        // At most 2000 points: a long stroke keeps every n-th.
        const every = Math.ceil(d.pts.length / 2000)
        d.pts.forEach((q, i) => {
          if (i % every === 0 || i === d.pts.length - 1) pts.push(Math.round((q.x - box.x) * 10) / 10, Math.round((q.y - box.y) * 10) / 10)
        })
        write([{ id: newId(), set: { t: 'pen', x: box.x, y: box.y, w: Math.max(1, box.w), h: Math.max(1, box.h), pts, stroke: INK, sw: 3, z: topZ(elsRef.current) + 1 } }])
        return
      }
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId)
    if (pinch.current) {
      if (pointers.current.size < 2) pinch.current = null
      return
    }
    const p = toWorld(e.clientX, e.clientY)
    finishDrag(false, p, e)
    onTouchTap(e)
  }

  // A double-click edits the words of what it lands on. On the bare board it makes nothing: a note comes from its tool
  // only (owner, 2026-11-02: 只要點擊螢幕某個地方一下, 就會彈出筆記便條貼, 這是非常不好的使用者體驗 — two taps on a phone
  // are a double-click).
  const onDoubleClick = (e: React.MouseEvent) => {
    if (readOnly) return
    const hit = hitTest(elsRef.current, toWorld(e.clientX, e.clientY), slack)
    if (hit && writesText(hit)) startEdit(hit.id)
  }

  // ── what is drawn over the board ────────────────────────────────────────

  const order = useMemo(() => paintOrder(els), [els])
  const selected = sel.map((id) => els[id]).filter((x): x is El => !!x)
  const selBox = union(selected.map((x) => boxOf(x, els)).filter((b): b is Box => !!b))
  const one = selected.length === 1 ? selected[0]! : null
  const k = 1 / cam.s
  const d = drag

  const editEl = editing ? els[editing.id] : null
  const editor = (() => {
    if (!editing || !editEl) return null
    if (editEl.t === 'line') {
      // A label is typed where it will stand: centred on the line's middle, on white as the drawn label is, so the
      // line does not run through the words (owner, 2026-10-30, iPad: the label showed twice and crossed by the line).
      const g = lineGeom(editEl, els)
      if (!g) return null
      const m = midpoint(g)
      const fsB = editEl.fs ?? DEFAULT_FS['line']!
      const rows = (editing.text || ' ').split('\n')
      const wB = Math.max(160, ...rows.map((l) => textWidth(l, fsB, editEl.bold) + 24))
      const hB = rows.length * fsB * LINE_HEIGHT + 8
      return {
        left: (m.x - wB / 2) * cam.s + cam.x,
        top: (m.y - hB / 2) * cam.s + cam.y,
        width: wB * cam.s,
        height: hB * cam.s,
        fs: fsB * cam.s,
        align: 'center' as const,
        el: editEl,
        pad: 4 * cam.s,
      }
    }
    if (editEl.t === 'frame') {
      return { left: editEl.x * cam.s + cam.x, top: (editEl.y - 30) * cam.s + cam.y, width: Math.max(160, editEl.w * cam.s * 0.6), height: 26 * cam.s, fs: 14 * cam.s, align: 'left' as const, el: editEl }
    }
    const tb = textBox(editEl)
    const { fs } = layoutText({ ...editEl, text: editing.text || ' ' })
    return {
      left: (editEl.x + tb.x) * cam.s + cam.x,
      top: (editEl.y + tb.y) * cam.s + cam.y,
      width: tb.w * cam.s,
      height: Math.max(tb.h, fs * LINE_HEIGHT) * cam.s,
      fs: fs * cam.s,
      align: editEl.align ?? (editEl.t === 'text' ? ('left' as const) : ('center' as const)),
      el: editEl,
    }
  })()

  const ctxStyle: React.CSSProperties | null = (() => {
    if (!selBox || editing || d) return null
    if (phone) return { left: 8, right: 8, top: 8 }
    const left = Math.min(Math.max(8, (selBox.x + selBox.w / 2) * cam.s + cam.x), size.w - 8)
    // Above the selection, clear of its dots; under it when there is no room above (the bar is ≤ 80 px tall).
    const top = selBox.y * cam.s + cam.y - 34
    if (top < 90) return { left, top: (selBox.y + selBox.h) * cam.s + cam.y + 34, transform: 'translateX(-50%)' }
    return { left, top, transform: 'translate(-50%, -100%)' }
  })()

  const empty = board.status === 'ready' && order.length === 0
  const cursor = d?.kind === 'pan' || space || tool === 'hand' ? (d?.kind === 'pan' ? 'grabbing' : 'grab') : tool === 'select' ? 'default' : 'crosshair'
  const grid = 24 * cam.s
  const showGrid = grid >= 8

  return (
    <div className="bd-wrap">
      <div
        ref={stage}
        className="bd-stage"
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
        onDragOver={(e) => {
          if (readOnly || ![...e.dataTransfer.types].includes('Files')) return
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
        }}
        onDrop={(e) => {
          if (readOnly || !e.dataTransfer.files.length) return
          e.preventDefault()
          void importImages([...e.dataTransfer.files], toWorld(e.clientX, e.clientY))
        }}
      >
        <svg className="bd-svg" width={size.w} height={size.h}>
          <defs>
            <pattern id="bd-dots" width={grid} height={grid} patternUnits="userSpaceOnUse" x={cam.x % grid} y={cam.y % grid}>
              <circle cx={1} cy={1} r={1} fill="#cfd4da" />
            </pattern>
          </defs>
          {showGrid && <rect width={size.w} height={size.h} fill="url(#bd-dots)" />}
          <g transform={`translate(${cam.x},${cam.y}) scale(${cam.s})`}>
            <g ref={layer}>
              {order.map((el) =>
                el.t === 'line' ? (
                  <LineView key={el.id} el={el} els={els} faded={d?.kind === 'end' && d.id === el.id} editing={editing?.id === el.id} />
                ) : (
                  <ElementShape key={el.id} el={el} editing={editing?.id === el.id} />
                ),
              )}
            </g>

            {/* questions about areas (area.ts): the one being asked, one just sent, kept ones, one shown from the chat */}
            {[
              ...(asking ? [{ key: 'asking', b: asking.area, cls: 'bd-area' }] : []),
              ...(sent ? [{ key: 'sent', b: sent, cls: 'bd-area bd-area-sent' }] : []),
              ...(shown ? [{ key: 'shown', b: shown.area, cls: 'bd-area' }] : []),
              ...kept.filter((q) => !q.hidden).map((q) => ({ key: `k-${q.id}`, b: q as Box, cls: 'bd-area bd-area-kept' })),
            ].map(({ key, b, cls }) => (
              <rect
                key={key}
                className={cls}
                x={b.x}
                y={b.y}
                width={b.w}
                height={b.h}
                rx={6 * k}
                fill="rgba(139,92,246,0.06)"
                stroke={ASK}
                strokeWidth={2 * k}
                strokeDasharray={`${8 * k} ${5 * k}`}
                pointerEvents="none"
              />
            ))}

            {/* what is selected */}
            {selected.map((x) => {
              if (x.t === 'line') {
                const g = lineGeom(x, els)
                if (!g) return null
                return (
                  <g key={`s-${x.id}`} pointerEvents="none">
                    <path d={`M${g.pts.map((q) => `${q.x},${q.y}`).join(' L')}`} fill="none" stroke={SELECT} strokeWidth={2 * k} opacity={0.6} />
                  </g>
                )
              }
              return <rect key={`s-${x.id}`} x={x.x} y={x.y} width={x.w} height={x.h} fill="none" stroke={SELECT} strokeWidth={1.5 * k} pointerEvents="none" />
            })}
            {selBox && selected.length > 1 && (
              <rect x={selBox.x - 4 * k} y={selBox.y - 4 * k} width={selBox.w + 8 * k} height={selBox.h + 8 * k} fill="none" stroke={SELECT} strokeWidth={k} strokeDasharray={`${4 * k} ${3 * k}`} pointerEvents="none" />
            )}
            {one && one.t !== 'line' && !editing && !d && !readOnly && (
              <g>
                {(['nw', 'ne', 'sw', 'se'] as const).map((c) => {
                  const cx = c.includes('w') ? one.x : one.x + one.w
                  const cy = c.includes('n') ? one.y : one.y + one.h
                  return (
                    <rect
                      key={c}
                      data-h={c}
                      x={cx - 5 * k}
                      y={cy - 5 * k}
                      width={10 * k}
                      height={10 * k}
                      rx={2 * k}
                      fill="#ffffff"
                      stroke={SELECT}
                      strokeWidth={1.5 * k}
                      style={{ cursor: c === 'nw' || c === 'se' ? 'nwse-resize' : 'nesw-resize' }}
                    />
                  )
                })}
                {attachable(one) &&
                  one.t !== 'frame' &&
                  (['top', 'right', 'bottom', 'left'] as Side[]).map((s) => {
                    const sp = sidePoint(one, s)
                    const off = 22 * k
                    const q = { x: sp.x + (s === 'right' ? off : s === 'left' ? -off : 0), y: sp.y + (s === 'bottom' ? off : s === 'top' ? -off : 0) }
                    return (
                      <g key={s} data-h={`dot-${s}`} style={{ cursor: 'pointer' }}>
                        <circle cx={q.x} cy={q.y} r={14 * k} fill="transparent" />
                        <circle className="bd-dot-handle" cx={q.x} cy={q.y} r={6 * k} fill={SELECT} stroke="#ffffff" strokeWidth={2 * k} />
                      </g>
                    )
                  })}
              </g>
            )}
            {one && one.t === 'line' && !d && !readOnly && (() => {
              const g = lineGeom(one, els)
              if (!g) return null
              return (['a', 'b'] as const).map((w) => {
                const q = w === 'a' ? g.a.p : g.b.p
                return <circle key={w} data-h={`end-${w}`} cx={q.x} cy={q.y} r={7 * k} fill="#ffffff" stroke={SELECT} strokeWidth={2 * k} style={{ cursor: 'move' }} />
              })
            })()}

            {/* what a drag is doing */}
            {d?.kind === 'marquee' && (
              <rect
                x={Math.min(d.start.x, d.at.x)}
                y={Math.min(d.start.y, d.at.y)}
                width={Math.abs(d.at.x - d.start.x)}
                height={Math.abs(d.at.y - d.start.y)}
                fill="rgba(66,98,255,0.08)"
                stroke={SELECT}
                strokeWidth={k}
              />
            )}
            {d?.kind === 'create' && (Math.abs(d.at.x - d.start.x) > 4 * k || Math.abs(d.at.y - d.start.y) > 4 * k) && (
              <rect
                x={Math.min(d.start.x, d.at.x)}
                y={Math.min(d.start.y, d.at.y)}
                width={Math.abs(d.at.x - d.start.x)}
                height={Math.abs(d.at.y - d.start.y)}
                fill="none"
                stroke={SELECT}
                strokeWidth={1.5 * k}
                strokeDasharray={`${4 * k} ${3 * k}`}
              />
            )}
            {d?.kind === 'area' && (Math.abs(d.at.x - d.start.x) > 4 * k || Math.abs(d.at.y - d.start.y) > 4 * k) && (
              <rect
                x={Math.min(d.start.x, d.at.x)}
                y={Math.min(d.start.y, d.at.y)}
                width={Math.abs(d.at.x - d.start.x)}
                height={Math.abs(d.at.y - d.start.y)}
                rx={6 * k}
                fill="rgba(139,92,246,0.06)"
                stroke={ASK}
                strokeWidth={2 * k}
                strokeDasharray={`${8 * k} ${5 * k}`}
              />
            )}
            {(d?.kind === 'line' || d?.kind === 'end') && (() => {
              const draft: El =
                d.kind === 'line'
                  ? { id: '_draft', t: 'line', x: 0, y: 0, w: 1, h: 1, z: 0, a: d.from, b: d.target ?? { x: d.at.x, y: d.at.y }, route: 'elbow', head: 'arrow', stroke: SELECT }
                  : { ...els[d.id]!, id: '_draft', [d.which]: d.target ?? { x: d.at.x, y: d.at.y }, stroke: SELECT }
              const target = d.target ? els[d.target.id] : null
              // Where the dragged end will hold: on the edge it is over, or nowhere yet (a free end follows the pointer).
              const g = target ? lineGeom(draft, els) : null
              const held = g ? (d.kind === 'line' || d.which === 'b' ? g.b.p : g.a.p) : null
              return (
                <g pointerEvents="none">
                  {target && <rect x={target.x - 3 * k} y={target.y - 3 * k} width={target.w + 6 * k} height={target.h + 6 * k} fill="none" stroke={SELECT} strokeWidth={2 * k} rx={4 * k} />}
                  <LineView el={draft} els={els} />
                  {held && d.target?.side && <circle className="bd-held" cx={held.x} cy={held.y} r={5 * k} fill="#ffffff" stroke={SELECT} strokeWidth={2 * k} />}
                </g>
              )
            })()}
            {d?.kind === 'pen' && d.pts.length > 1 && (
              <path d={`M${d.pts.map((q) => `${q.x},${q.y}`).join(' L')}`} fill="none" stroke={INK} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
            )}
          </g>
        </svg>

        {editor && editor.el.t === 'sticky' && (
          // A note is written as a page is (owner, 2026-11-02: 便條貼 … 預覽編輯 Markdown … 就如同我開給你看的 Notion 頁面一樣).
          <Suspense fallback={null}>
            <NoteEditor
              key={editing!.id}
              markdown={editing!.text}
              onChange={(md) => setEditing((cur) => (cur ? { ...cur, text: md } : cur))}
              onDone={commitEdit}
              style={{
                left: editor.left,
                top: editor.top,
                width: editor.width,
                height: editor.height,
                fontSize: editor.fs,
                fontWeight: editor.el.bold ? 600 : 400,
                color: editor.el.color ?? INK,
              }}
            />
          </Suspense>
        )}
        {editor && editor.el.t !== 'sticky' && (
          <textarea
            className="bd-editor"
            autoFocus
            value={editing!.text}
            spellCheck={false}
            style={{
              left: editor.left,
              top: editor.top,
              width: editor.width,
              minHeight: editor.height,
              // Exactly the label's rows: a textarea's own default is two, which pushed it below the line's middle.
              ...('pad' in editor
                ? { height: editor.height, padding: `${editor.pad}px 0`, boxSizing: 'border-box' as const, background: '#ffffff' }
                : {}),
              fontSize: editor.fs,
              lineHeight: LINE_HEIGHT,
              fontFamily: BOARD_FONT,
              fontWeight: editor.el.bold ? 600 : 400,
              textAlign: editor.align,
              color: editor.el.color ?? INK,
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onChange={(e) => setEditing((cur) => (cur ? { ...cur, text: e.target.value } : cur))}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey))) {
                e.preventDefault()
                commitEdit()
              }
              if (e.key === 'Tab' && editing && !readOnly) {
                // Tab: the next box of the flowchart, to the right (as in Miro).
                e.preventDefault()
                const id = editing.id
                commitEdit()
                const el = elsRef.current[id]
                if (el && el.t !== 'line' && el.t !== 'frame') quickConnect(id, 'right')
              }
            }}
          />
        )}

        {empty && !d && (
          <div className="bd-empty" aria-live="polite">
            <strong>An empty board</strong>
            <span>{phone ? 'Pick a sticky note or a shape below, then drag on the board to place it.' : 'Pick a sticky note or a shape at the left, then drag on the board to place it.'}</span>
            <span>Agents in this project can draw here too: ask one in the chat to “draw the flow on the whiteboard”.</span>
          </div>
        )}
        {board.status === 'loading' && <div className="bd-empty">Loading the board…</div>}
        {uploading > 0 && (
          <div className="bd-uploading" role="status">
            {uploading === 1 ? 'Adding a picture…' : `Adding ${uploading} pictures…`}
          </div>
        )}
        {board.status === 'error' && (
          <div className="bd-empty">
            <strong>The board did not load.</strong>
            <button type="button" className="sheet-btn" onClick={() => void board.reload()}>
              Try again
            </button>
          </div>
        )}
      </div>

      {readOnly && ask && <AskOnlyToolbar tool={tool} setTool={setTool} />}
      {!readOnly && (
        <Toolbar
          tool={tool}
          setTool={(t) => {
            setTool(t)
            if (t !== 'select') setSel([])
          }}
          shape={shape}
          setShape={setShape}
          note={note}
          setNote={setNote}
          canUndo={board.canUndo}
          canRedo={board.canRedo}
          onUndo={board.undo}
          onRedo={board.redo}
          onImage={() => fileInput.current?.click()}
          canAsk={!!ask}
        />
      )}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          void importImages(files)
        }}
      />
      {ctxStyle && selected.length > 0 && !readOnly && (
        <ContextBar
          key={sel.join(',')}
          sel={selected}
          style={ctxStyle}
          onChange={write}
          onDelete={() => deleteIds(sel)}
          onDuplicate={duplicate}
          onFront={() => restack(true)}
          onBack={() => restack(false)}
        />
      )}
      {ask && (
        <AreaOverlays
          cam={cam}
          size={size}
          phone={phone}
          convId={convId}
          boardId={boardId}
          ask={ask}
          asking={asking}
          sent={sent}
          shown={shown}
          kept={kept}
          openKept={openKept}
          picture={(area) => areaPicture(layer.current, area)}
          onCancel={() => setAsking(null)}
          onSent={(q) => {
            const a = asking
            setAsking(null)
            if (!a) return
            if (q.keep) setKept((cur) => [...cur.filter((x) => x.id !== q.id), { ...a.area, id: q.id, text: q.text, ts: Date.now() }])
            else setSent(a.area)
          }}
          onDismissShown={() => setShown(null)}
          onOpenKept={(id) => setOpenKept((cur) => (cur === id ? null : id))}
          onHide={(id) => {
            setKept((cur) => cur.map((x) => (x.id === id ? { ...x, hidden: true } : x)))
            setOpenKept(null)
          }}
          onUnhide={(id) => setKept((cur) => cur.map((x) => (x.id === id ? { ...x, hidden: false } : x)))}
          onDelete={(id) => {
            setKept((cur) => cur.filter((x) => x.id !== id))
            setOpenKept(null)
          }}
        />
      )}
      <ZoomBar scale={cam.s} onZoom={(f) => zoomAt(f)} onFit={fit} onReset={() => zoomAt(1 / cam.s)} />
    </div>
  )
}

/** A board box on the screen. */
function onScreen(b: Box, cam: Cam): Box {
  return { x: b.x * cam.s + cam.x, y: b.y * cam.s + cam.y, w: b.w * cam.s, h: b.h * cam.s }
}

/** The first line of a question, short enough for a tag. */
function gist(text: string, n = 48): string {
  const line = text.split('\n')[0]!.trim()
  const chars = Array.from(line)
  return chars.length > n ? `${chars.slice(0, n - 1).join('')}…` : line
}

/**
 * What stands over the board for questions about its areas (area.ts): the window of the one being asked, a word that
 * one just went, the tag of an area shown again from the chat, and each kept question — its tag (pressed: the card
 * with Hide and Delete), or, hidden, a small pin where it was (pressed: back).
 */
function AreaOverlays({
  cam,
  size,
  phone,
  convId,
  boardId,
  ask,
  asking,
  sent,
  shown,
  kept,
  openKept,
  picture,
  onCancel,
  onSent,
  onDismissShown,
  onOpenKept,
  onHide,
  onUnhide,
  onDelete,
}: {
  cam: Cam
  size: { w: number; h: number }
  phone: boolean
  convId: string
  boardId: string
  ask: { boardName: string; agents: MentionAgent[]; onOpenChat: () => void }
  asking: { area: Box; ids: string[] } | null
  sent: Box | null
  shown: Shown | null
  kept: KeptAsk[]
  openKept: string | null
  picture: (area: Box) => Promise<Blob | null>
  onCancel: () => void
  onSent: (q: { id: string; text: string; keep: boolean }) => void
  onDismissShown: () => void
  onOpenKept: (id: string) => void
  onHide: (id: string) => void
  onUnhide: (id: string) => void
  onDelete: (id: string) => void
}) {
  const tagAt = (b: Box): React.CSSProperties => {
    const r = onScreen(b, cam)
    return { left: r.x, top: r.y - 30 }
  }
  const askStyle: React.CSSProperties | null = (() => {
    if (!asking) return null
    if (phone) return { left: 8, right: 8, top: 8 }
    const at = placeAsk(onScreen(asking.area, cam), size, ASK_WIN)
    return { left: at.left, top: at.top, width: ASK_WIN.w }
  })()
  return (
    <>
      {asking && askStyle && (
        <AskBox
          convId={convId}
          board={boardId}
          boardName={ask.boardName}
          area={asking.area}
          ids={asking.ids}
          agents={ask.agents}
          style={askStyle}
          picture={() => picture(asking.area)}
          onSent={onSent}
          onCancel={onCancel}
        />
      )}
      {sent && (
        <div className="bd-area-tag bd-area-done" style={tagAt(sent)} role="status">
          <Icon name="check" size={14} />
          <span>Sent to the project chat</span>
        </div>
      )}
      {shown && (
        <div className="bd-area-tag" style={tagAt(shown.area)} role="status" onPointerDown={(e) => e.stopPropagation()}>
          <AskGlyph size={14} />
          <span className="bd-area-gist">{gist(shown.text) || 'Asked about this area'}</span>
          <button type="button" aria-label="Stop showing this area" onClick={onDismissShown}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
      {kept.map((q) =>
        q.hidden ? (
          <button
            key={q.id}
            type="button"
            className="bd-area-pin"
            style={(() => {
              const r = onScreen(q, cam)
              return { left: r.x - 13, top: r.y - 13 }
            })()}
            title={`Show: ${gist(q.text)}`}
            aria-label={`Show the area you asked about: ${gist(q.text)}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onUnhide(q.id)}
          >
            <AskGlyph size={15} />
          </button>
        ) : (
          <div key={q.id} className="bd-area-keep" style={tagAt(q)} onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" className="bd-area-tag bd-area-tag-btn" aria-expanded={openKept === q.id} onClick={() => onOpenKept(q.id)}>
              <AskGlyph size={14} />
              <span className="bd-area-gist">{gist(q.text)}</span>
            </button>
            {openKept === q.id && (
              <div className="bd-area-card" role="dialog" aria-label="The question about this area">
                <p>{q.text}</p>
                <div className="bd-area-acts">
                  <button type="button" onClick={ask.onOpenChat}>
                    <Icon name="chat" size={15} />
                    <span>Answers in chat</span>
                  </button>
                  <button type="button" onClick={() => onHide(q.id)}>
                    <Icon name="visibility_off" size={15} />
                    <span>Hide</span>
                  </button>
                  <button type="button" className="bd-danger" onClick={() => onDelete(q.id)}>
                    <Icon name="delete" size={15} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ),
      )}
    </>
  )
}
